/**
 * R2MirrorStatus — admin tile for the Cloudflare R2 content-mirror.
 *
 * Why this exists
 * ---------------
 * Production and preview each have their own ephemeral filesystem.
 * An image rendered by preview's worker doesn't exist on prod's disk,
 * so `/api/content-images/{id}` returns "Image file missing on disk"
 * the moment cross-env image access kicks in (which happens any time
 * an admin previews a pin on production after a preview-side render).
 *
 * The fix is the Cloudflare R2 mirror — the image pipeline uploads
 * every rendered PNG to R2 and stores its CDN URL on the asset
 * record; the serve endpoint 302-redirects to that URL when the
 * local file is missing. This tile surfaces the mirror's health so
 * admins can SEE when R2 is broken or missing assets, and one-click
 * backfill stragglers.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CloudUpload, AlertTriangle, CheckCircle2, RefreshCw, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";


export default function R2MirrorStatus() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [backfilling, setBackfilling] = useState(false);

    async function loadStatus(silent = false) {
        if (!silent) setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/admin/ops/r2-content-mirror/status`,
                { withCredentials: true },
            );
            setStatus(data || null);
        } catch (e) {
            if (!silent) toast.error("Failed to load R2 mirror status");
        } finally {
            setLoading(false);
        }
    }

    async function handleBackfill() {
        setBackfilling(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/ops/r2-content-mirror/backfill`,
                {},
                { withCredentials: true, params: { limit: 50 } },
            );
            if (data?.ok === false) {
                toast.error(data.reason || "Backfill skipped");
            } else {
                const up = Number(data?.uploaded) || 0;
                const sk = Number(data?.skipped_local_missing) || 0;
                const fl = Number(data?.failed) || 0;
                toast.success(
                    `Uploaded ${up} · skipped ${sk} (local missing) · failed ${fl}`,
                );
            }
            await loadStatus(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backfill failed");
        } finally {
            setBackfilling(false);
        }
    }

    useEffect(() => {
        loadStatus();
    }, []);

    if (loading) {
        return (
            <div className="bl-card p-4 text-sm text-bl-muted" data-testid="r2-mirror-status-loading">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                Loading R2 mirror status…
            </div>
        );
    }

    if (!status) return null;

    const enabled = !!status.enabled;
    const pending = Number(status.backfill_pending) || 0;
    const coverage = status.coverage_pct;

    return (
        <div className="bl-card p-5 space-y-3" data-testid="r2-mirror-status-tile">
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                        <CloudUpload className="h-4 w-4 text-bl-teal" />
                        <h3 className="text-base font-semibold text-bl">
                            R2 image mirror
                        </h3>
                        <span
                            className={`flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                                enabled
                                    ? "bg-bl-green-soft text-bl-green"
                                    : "bg-bl-amber-tint text-bl-amber"
                            }`}
                            data-testid="r2-mirror-status-badge"
                        >
                            <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                    enabled ? "bg-bl-green animate-pulse" : "bg-bl-amber"
                                }`}
                            />
                            {enabled ? "Configured" : "Not configured"}
                        </span>
                    </div>
                    <p className="text-xs text-bl-muted">
                        {enabled ? (
                            <>
                                Bucket <span className="font-mono-bl">{status.bucket || "(unset)"}</span> · {coverage == null ? "no recent assets" : `${coverage}% of last-30d assets mirrored`}
                            </>
                        ) : (
                            <>
                                Production can&apos;t serve preview-rendered images without R2. Set the <span className="font-mono-bl">R2_CONTENT_*</span> env vars in your deploy panel.
                            </>
                        )}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => loadStatus(false)}
                    className="bl-btn-ghost text-xs px-2 py-1.5 flex items-center gap-1.5"
                    data-testid="r2-mirror-refresh-btn"
                    aria-label="Refresh R2 mirror status"
                >
                    <RefreshCw className="h-3.5 w-3.5" />
                </button>
            </div>

            {!enabled && (
                <div className="text-xs text-bl-muted bg-bl-amber-tint/40 border border-bl-amber/30 rounded-md p-3 space-y-1.5">
                    <div className="flex items-center gap-1.5 font-semibold text-bl-amber">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Setup required
                    </div>
                    <ol className="list-decimal pl-5 space-y-0.5">
                        <li>Create a Cloudflare R2 bucket (e.g. <span className="font-mono-bl">buyersleverage-content</span>).</li>
                        <li>Enable a public custom domain or use <span className="font-mono-bl">pub-*.r2.dev</span>.</li>
                        <li>In the deployment panel set <span className="font-mono-bl">R2_CONTENT_BUCKET</span>, <span className="font-mono-bl">R2_CONTENT_ENDPOINT_URL</span>, <span className="font-mono-bl">R2_CONTENT_ACCESS_KEY_ID</span>, <span className="font-mono-bl">R2_CONTENT_SECRET_ACCESS_KEY</span>, <span className="font-mono-bl">R2_CONTENT_PUBLIC_BASE_URL</span>.</li>
                        <li>Redeploy, then click &quot;Backfill missing&quot; below.</li>
                    </ol>
                </div>
            )}

            {pending > 0 && (
                <button
                    type="button"
                    onClick={handleBackfill}
                    disabled={backfilling || !enabled}
                    className="w-full bl-btn-secondary text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="r2-mirror-backfill-btn"
                    title={
                        enabled
                            ? `Upload up to 50 local PNGs per click. ${pending} pending overall.`
                            : "R2 must be configured first"
                    }
                >
                    {backfilling ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <CloudUpload className="h-4 w-4" />
                    )}
                    Backfill {Math.min(pending, 50)} of {pending} missing
                </button>
            )}

            {enabled && pending === 0 && (
                <div className="flex items-center gap-2 text-xs text-bl-green">
                    <CheckCircle2 className="h-4 w-4" />
                    All generated assets mirrored to R2.
                </div>
            )}
        </div>
    );
}
